/**
 * F-148 §4.5 (AC8): the barcode and the variant ID resolve to different variants. The line stays
 * blocked until the merchant picks one side explicitly (`matchMethod = 'manual'`).
 */
import { useTranslation } from '@dukanti/shared';
import type { ReceivingVariantSnapshot } from '@dukanti/shared/admin';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface IdentifierConflictPanelProps {
  readonly byBarcode: ReceivingVariantSnapshot | null;
  readonly byRef: ReceivingVariantSnapshot | null;
  readonly onResolve: (variant: ReceivingVariantSnapshot) => void;
  readonly disabled?: boolean;
  readonly className?: string;
}

function Candidate({
  label,
  variant,
  onUse,
  disabled,
  testId,
}: {
  label: string;
  variant: ReceivingVariantSnapshot | null;
  onUse: (variant: ReceivingVariantSnapshot) => void;
  disabled: boolean;
  testId: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      className="border-border bg-card flex min-w-0 flex-col gap-2 rounded-lg border p-3"
      data-testid={testId}
    >
      <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">{label}</p>
      {variant ? (
        <>
          <p className="text-foreground truncate text-sm font-medium">
            <bdi>{variant.productName}</bdi>
          </p>
          {variant.optionSummary ? (
            <p className="text-muted-foreground truncate text-xs">
              <bdi>{variant.optionSummary}</bdi>
            </p>
          ) : null}
          {/* `Badge` renders a div, so this row is a div (not a p) to keep the DOM valid. */}
          <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
            <span>
              {t('admin.barcode.result.stock')}: <bdi dir="ltr">{variant.inventory}</bdi>
            </span>
            {!variant.active ? (
              <Badge variant="neutral">{t('admin.receiving.line.inactiveBadge')}</Badge>
            ) : null}
          </div>
          {variant.barcode ? (
            <p className="text-muted-foreground font-mono text-xs">
              <bdi dir="ltr">{variant.barcode}</bdi>
            </p>
          ) : null}
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            disabled={disabled || !variant.eligible}
            onClick={() => onUse(variant)}
            data-testid={`${testId}-use`}
          >
            {t('admin.receiving.conflict.use')}
          </Button>
        </>
      ) : (
        <p className="text-muted-foreground text-sm">{t('admin.receiving.line.noVariant')}</p>
      )}
    </div>
  );
}

export function IdentifierConflictPanel({
  byBarcode,
  byRef,
  onResolve,
  disabled = false,
  className,
}: IdentifierConflictPanelProps): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <section
      role="group"
      aria-labelledby={undefined}
      aria-label={t('admin.receiving.conflict.title')}
      data-testid="identifier-conflict-panel"
      className={cn(
        'rounded-lg border border-amber-200 bg-amber-50 p-3 text-start dark:border-amber-500/30 dark:bg-amber-500/15',
        className,
      )}
    >
      <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
        {t('admin.receiving.conflict.title')}
      </p>
      <p className="mt-1 text-xs text-amber-900/90 dark:text-amber-100/90">
        {t('admin.receiving.conflict.description')}
      </p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Candidate
          label={t('admin.receiving.conflict.byBarcode')}
          variant={byBarcode}
          onUse={onResolve}
          disabled={disabled}
          testId="conflict-by-barcode"
        />
        <Candidate
          label={t('admin.receiving.conflict.byRef')}
          variant={byRef}
          onUse={onResolve}
          disabled={disabled}
          testId="conflict-by-ref"
        />
      </div>
    </section>
  );
}
